import { useMemo, useState } from "react";
import { format } from "date-fns";
import MonthPicker from "../../../../../shared/components/MonthPicker";
import { Skeleton } from "../../../../../shared/components/ui/skeleton";
import { useFiatTransactions } from "@/features/admin/services/fiat";
import BankingTransactionItem from "./BankingTransactionItem";

const formatAmount = (
  value: string | number,
  currency: string,
  positive: boolean
) => {
  const numeric = Number.parseFloat(String(value));
  const safeNumber = Number.isFinite(numeric) ? numeric : 0;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  });
  const formatted = formatter.format(safeNumber);
  return `${positive ? "+" : "-"}${formatted.replace(/^[+-]/, "")}`;
};

const formatDate = (value?: string) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy HH:mm");
  } catch {
    return value;
  }
};

export default function BankingFundsHistory() {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  });

  const {
    data: transactions = [],
    isLoading,
    isFetching,
    error,
  } = useFiatTransactions(filters);

  const rows = useMemo(() => {
    return transactions.map((tx) => {
      const currency = tx.currency?.toUpperCase() || "USD";
      const positive = tx.type === "fiatDeposit" || tx.type === "transfer";
      const title =
        tx.type === "fiatDeposit"
          ? "Fiat Deposit"
          : tx.type === "fiatWithdrawal"
            ? "Fiat Withdrawal"
            : "Transfer";
      const subtitle = tx.description || formatDate(tx.createdAt);
      return {
        id: tx.id,
        title,
        subtitle,
        amount: formatAmount(tx.amount, currency, positive),
        currency,
        positive,
        dateLabel: formatDate(tx.createdAt),
      };
    });
  }, [transactions]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white">Transaction History</h3>
        <MonthPicker value={filters} onChange={setFilters} />
      </div>

      {error ? (
        <p className="text-sm text-red-400">
          Unable to load transactions right now.
        </p>
      ) : isLoading || (isFetching && !transactions.length) ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton
              key={`bank-tx-skel-${idx}`}
              className="h-12 w-full rounded-lg bg-white/10"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-white/60">No transactions yet.</p>
      ) : (
        <div className="flex flex-col divide-y divide-white/10">
          {rows.map((row) => (
            <BankingTransactionItem
              key={row.id}
              icon={<span>{row.currency.slice(0, 3)}</span>}
              title={row.title}
              subtitle={row.subtitle}
              amount={row.amount}
              currency={row.currency}
              positive={row.positive}
            />
          ))}
        </div>
      )}
    </div>
  );
}
